<template>
  <div class="billing-operation-mode">
    <operation-context-alert>
      Bu adım müşteri için yetkili kişiyi atar. Fatura tutarını değiştirmez;
      operasyonel iletişim bilgisini tamamlar.
    </operation-context-alert>

    <operation-summary-strip :items="summaryItems" />

    <operation-panel
      title="Yetkili kişiyi seç"
      subtitle="Gecikmiş ödeme ve operasyon iletişimlerinde kullanılacak kullanıcı atanır."
      tone="primary"
      chip-label="Adım"
      chip-color="primary"
    >
      <div class="operation-field-grid operation-field-grid--two">
        <div class="operation-field">
          <div class="operation-field__label">Yetkili kişi</div>
          <v-autocomplete
            v-if="hasCandidates"
            class="operation-field-input operation-field-input--compact"
            :value="authorizedUser.authorized_user_id"
            :items="candidateUsers"
            :filter="filterCandidateUsers"
            item-text="name"
            item-value="id"
            label=""
            hide-details
            @input="handleAuthorizedUserInput"
          >
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title>
                  {{ data.item.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ data.item.source_detail || data.item.source_label }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <div v-else class="operation-field__readonly">
            <span>{{ scopePlaceholder }}</span>
          </div>
          <div class="operation-field__hint">
            {{ scopeHint }}
          </div>
        </div>
        <div class="operation-field">
          <div class="operation-field__label">Seçili kullanıcı</div>
          <div class="operation-field__readonly">
            <span>{{ selectedAuthorizedUserLabel }}</span>
          </div>
          <div class="operation-field__hint">
            {{ selectedAuthorizedUserDetail }}
          </div>
        </div>
      </div>
    </operation-panel>

    <operation-blocked-reasons
      v-if="blockedReasons.length"
      :reasons="blockedReasons"
      class="mb-4"
    />
  </div>
</template>

<script>
import OperationBlockedReasons from "../shared/OperationBlockedReasons.vue";
import OperationContextAlert from "../shared/OperationContextAlert.vue";
import OperationPanel from "../shared/OperationPanel.vue";
import OperationSummaryStrip from "../shared/OperationSummaryStrip.vue";

export default {
  name: "AuthorizedUserOperationMode",
  components: {
    OperationBlockedReasons,
    OperationContextAlert,
    OperationPanel,
    OperationSummaryStrip,
  },
  props: {
    draft: {
      type: Object,
      required: true,
    },
    preview: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedUserOption: null,
    };
  },
  computed: {
    customer() {
      return this.preview?.customer || {};
    },
    authorizedUser() {
      return this.draft.authorized_user || {};
    },
    currentAuthorizedUser() {
      return this.preview?.customer?.authorized_user || null;
    },
    selectedAuthorizedUser() {
      if (
        this.selectedUserOption &&
        this.selectedUserOption.id === this.authorizedUser.authorized_user_id
      ) {
        return this.selectedUserOption;
      }

      const matchedCandidate = this.candidateUsers.find(
        (candidate) => candidate.id === this.authorizedUser.authorized_user_id
      );

      if (matchedCandidate) {
        return matchedCandidate;
      }

      if (
        this.preview?.authorized_user &&
        this.preview.authorized_user.id ===
          this.authorizedUser.authorized_user_id
      ) {
        return this.preview.authorized_user;
      }

      return null;
    },
    selectedAuthorizedUserLabel() {
      return this.selectedAuthorizedUser?.name || "Seçim bekleniyor";
    },
    selectedAuthorizedUserDetail() {
      if (!this.selectedAuthorizedUser) {
        return "İletişim bilgisi yok";
      }

      const detail = [
        this.selectedAuthorizedUser.source_detail,
        this.selectedAuthorizedUser.email,
        this.selectedAuthorizedUser.phone,
      ]
        .filter(Boolean)
        .join(" | ");

      return detail || "İletişim bilgisi yok";
    },
    blockedReasons() {
      return this.preview?.resolution_plan?.blocked_reasons || [];
    },
    candidateScope() {
      return this.preview?.candidate_scope || {};
    },
    candidateUsers() {
      return Array.isArray(this.preview?.candidate_users)
        ? this.preview.candidate_users
        : [];
    },
    ownedClusters() {
      return this.preview?.owned_clusters || [];
    },
    hasCandidates() {
      return this.candidateUsers.length > 0;
    },
    scopePlaceholder() {
      if (!this.preview) {
        return "Aday listesi yükleniyor";
      }

      return "Bu müşteri için kullanıcı adayı bulunamadı";
    },
    scopeHint() {
      if (!this.preview) {
        return "Müşteri kapsamına göre adaylar hazırlanıyor";
      }

      if (!this.hasCandidates) {
        return "Bu müşteri kapsamında atanabilir yetkili kişi adayı bulunamadı";
      }

      const parts = [];

      if (this.candidateScope.self_managed_cluster_count) {
        parts.push(
          `${this.candidateScope.self_managed_cluster_count} kendi yöneten TYA`
        );
      }

      if (this.candidateScope.managed_company_count) {
        parts.push(
          `${this.candidateScope.managed_company_count} yönetim şirketi`
        );
      }

      if (!parts.length) {
        return `${this.candidateUsers.length} aday kullanıcı listeleniyor`;
      }

      return `${parts.join(" + ")} kapsamından ${
        this.candidateUsers.length
      } aday kullanıcı listeleniyor`;
    },
    summaryItems() {
      return [
        {
          label: "Müşteri",
          value: this.customer.name || "-",
        },
        {
          label: "Muhasebe ID",
          value: this.customer.accounting_id || "-",
        },
        {
          label: "Mevcut yetkili",
          value: this.currentAuthorizedUser?.name || "Eksik",
        },
        {
          label: "Aday kullanıcı",
          value: `${this.candidateUsers.length} kayıt`,
        },
      ];
    },
  },
  methods: {
    updateAuthorizedUserField(field, value) {
      this.$emit("update-draft", {
        namespace: "authorized_user",
        field,
        value,
      });
    },
    handleAuthorizedUserInput(value) {
      const normalizedValue =
        value === null || value === undefined || value === ""
          ? null
          : Number(value);

      this.selectedUserOption =
        this.candidateUsers.find(
          (candidate) => candidate.id === normalizedValue
        ) || null;

      this.updateAuthorizedUserField("authorized_user_id", normalizedValue);
    },
    filterCandidateUsers(item, queryText) {
      const query = (queryText || "").toString().trim().toLocaleLowerCase("tr");

      if (!query) {
        return true;
      }

      return [
        item.name,
        item.email,
        item.phone,
        item.source_label,
        item.source_detail,
      ]
        .filter(Boolean)
        .some((value) =>
          value.toString().toLocaleLowerCase("tr").includes(query)
        );
    },
  },
};
</script>

<style src="../shared/operation-fields.css"></style>
